//#region src/components/Markdown/chat-markdown.ts
/**
* Bullet glyphs as markdown list items, for chat prose.
*
* A producer that writes `•` at the head of a line means a list item, but
* `•` is not a list marker in CommonMark, and with no `breaks` plugin a single
* newline is a soft break: the items run together on one line. This rewrites
* such lines as `- ` items on the reading side, so live prose and stored prose
* both read as lists, and leaves every other line as it came.
*
* `–` and `—` are not glyphs here: prose begins lines with them, and a false
* rewrite of prose costs more than a glyph left on screen.
*/
const BULLET_GLYPHS = [
	"•",
	"‣",
	"⁃",
	"·",
	"◦",
	"▪"
];
/** A fence opens or closes on a line that starts with ``` or ~~~. */
const FENCE_PATTERN = /^\s*(?:```|~~~)/;
/** A bullet line's indent and text, or nothing for any other line. */
const bulletParts = (line) => {
	const match = /^(\s*)(\S)(\s+)(.*)$/.exec(line);
	if (!match) return void 0;
	const [, indent = "", glyph = "", , rest = ""] = match;
	return BULLET_GLYPHS.includes(glyph) ? {
		indent,
		rest
	} : void 0;
};
/**
* Rewrites runs of bullet-glyph lines into markdown list items. A run opens
* with a blank line, as CommonMark needs to start a list; lines inside a
* fence stay as they are.
*
* @param text - Chat prose, as the producer wrote it
* @returns The same text with its bullet lines as list items; the same string
*   when it has none
*/
const chatMarkdown = (text) => {
	if (!BULLET_GLYPHS.some((glyph) => text.includes(glyph))) return text;
	const out = [];
	let inFence = false;
	let inRun = false;
	for (const line of text.split("\n")) {
		if (FENCE_PATTERN.test(line)) {
			inFence = !inFence;
			inRun = false;
			out.push(line);
			continue;
		}
		const parts = inFence ? void 0 : bulletParts(line);
		if (!parts) {
			inRun = false;
			out.push(line);
			continue;
		}
		if (!inRun && out.length > 0 && out.at(-1)?.trim() !== "") out.push("");
		out.push(`${parts.indent}- ${parts.rest}`);
		inRun = true;
	}
	return out.join("\n");
};
//#endregion
export { chatMarkdown };

//# sourceMappingURL=chat-markdown.js.map